/*
  Карточка должника — §3.3.5.

  Вся накопленная память о счёте: расчёт, давность, состав должников, путь по
  конвейеру, сформированные документы. Ни одна выгрузка из биллинга этого не
  содержит.

  🔴 **Разобрана на вкладки 21.09.2026, указание владельца**: «намешано сразу
  всё, непонятно, куда тыкать». До правки двенадцать блоков шли одной простынёй
  - состояние обращений, плитки, объяснение решения, подтверждение состава,
  должники, помещение, действия, приказ, его отмена, ссылка должнику, лента,
  документы, - и человек искал нужное прокруткой.

  Четыре вкладки в порядке работы: **Суд** (подача и собранные документы),
  **Приказ** (полученный приказ и его отмена), **Должники**, **История**.
  Документы стоят рядом с подачей, потому что это один заход: пакет собрали и
  тут же унесли файлы. Приказ отделён, потому что между подачей и приказом
  проходят недели, и карточку тогда открывают за другим.

  Что осталось НАД вкладками и почему именно это:

  1. **Что заявил должник.** Запрет обращаться действует сейчас, и спрятанный
     за вкладку он означал бы звонок человеку, который просил не звонить.
  2. **Четыре плитки денег** - то, ради чего карточку открывают.
  3. **Объяснение решения** - кнопкой, раскрывается на месте.

  Вкладка живёт в адресе страницы (`?tab=court`): ссылку можно переслать
  коллеге, а возврат из документа не сбрасывает её - то же правило, что у
  фильтров реестра (14.09.2026).

  Страница - контейнер: грузит карточку, держит сообщения и решает, что
  показать. Вся разметка блоков живёт в компонентах вкладок (§8.7 запрещает
  логику в экранах-контейнерах).
*/

import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import useAccountCard from '../components/useAccountCard.js'
import { assemblyOf, can } from '../components/access.js'
import { Loading, Notice } from '../components/Common.jsx'
import AccountRemoveButton from '../components/AccountRemoveButton.jsx'
import AccountContactActions from '../components/AccountContactActions.jsx'
import AccountCourtTab from '../components/AccountCourtTab.jsx'
import AccountStage from '../components/AccountStage.jsx'
import AccountEditButton from '../components/entry/AccountEditButton.jsx'
import AccountEntryTabs, { ENTRY_TABS } from '../components/entry/AccountEntryTabs.jsx'
import AccountOrderTab from '../components/AccountOrderTab.jsx'
import AccountPeopleTab from '../components/AccountPeopleTab.jsx'
import AccountRegistryFindings from '../components/AccountRegistryFindings.jsx'
import AccountSummary from '../components/AccountSummary.jsx'
import AccountWhy from '../components/AccountWhy.jsx'
import CustomerWork from '../components/CustomerWork.jsx'
import AccountTimeline from '../components/AccountTimeline.jsx'
import ContactState from '../components/ContactState.jsx'
import NeedsHumanNotice from '../components/NeedsHumanNotice.jsx'
import Tabs, { TabPanel } from '../components/Tabs.jsx'
import useCourtSections from '../components/useCourtSections.js'

/*
  Вкладки в порядке работы по счёту (указание владельца 21.09.2026): сначала то,
  ради чего карточку открывают чаще всего - подача и документы, - потом
  полученный приказ, потом состав должников, и последней история. История стоит
  в конце не по важности: её читают, когда разбираются, а не когда действуют.

  Вкладок «Суд» и «Приказ» нет у того, кто дело не ведёт, поэтому список
  собирается, а не объявлен постоянным.
*/
function tabsFor(card, canAct) {
  const tabs = []
  if (canAct) {
    tabs.push(
      {
        key: 'court',
        title: 'Суд',
        // Число собранных документов: они лежат на этой же вкладке, и значок
        // отвечает на вопрос «пакет уже собран?» до нажатия.
        // С 21.10.2026 карточка несёт только число: страницы документов читает вкладка.
        badge: card.documents_total > 0 ? String(card.documents_total) : null,
        badgeTitle: 'Сформированных документов по счёту',
      },
      { key: 'order', title: 'Приказ' },
    )
  }
  tabs.push(
    {
      key: 'people',
      title: 'Должники',
      // Значок вместо слов: содержимое невыбранной вкладки не рисуется, и без
      // него человек не узнает, что состав ждёт его подтверждения, пока сам
      // туда не зайдёт. Пока пометка стоит, счёт в волну не идёт.
      badge: card.participants_need_review ? '1' : null,
      badgeTitle: 'Состав должников ждёт подтверждения - без него счёт не идёт в подачу',
    },
    ...ENTRY_TABS,
    { key: 'history', title: 'История' },
  )
  return tabs
}

export default function AccountPage({ profile }) {
  const { accountId } = useParams()
  const [params, setParams] = useSearchParams()
  const [notice, setNotice] = useState(null)
  /*
    Кнопка строки «Дела по счёту» (указание владельца 06.10.2026) ведёт к блоку
    вкладок внизу: открывает нужную и прокручивает к ней, а форме смены стадии
    подсказывает следующую стадию. Отметка времени - чтобы второе нажатие той же
    кнопки снова прокрутило, хотя вкладка и стадия те же.
  */
  const [goal, setGoal] = useState(null)
  useEffect(() => {
    // Смена стадии - окно поверх страницы, прокручивать к вкладкам незачем.
    if (goal && !goal.stage) {
      document.getElementById('account-tabs')?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [goal])
  /*
    Заведённые участки грузит страница, а не поле: их спрашивают два блока сразу
    - сборка пакета и заведение приказа, - и запрос на каждое поле означал бы
    два одинаковых обращения при открытии одной карточки.
  */
  const { sections, error: sectionsError } = useCourtSections(
    profile.permissions.includes('build_documents'),
  )

  // Чтение, перечитывание и защита от запоздавшего ответа - в `useAccountCard`.
  const { card, error, reload } = useAccountCard(accountId)

  // Действие удалось: сказать об этом и перечитать карточку. Один обработчик на
  // четыре блока (17.09.2026): смена стадии, отмена приказа, удаление документа
  // и правка сведений о должнике меняют карточку целиком, а четыре копии одного
  // и того же успели бы разъехаться в мелочах.
  const done = (text, kind = 'ok') => {
    setNotice({ text, kind })
    reload()
  }

  /*
    🔴 Ошибка не оставляет человека на пустой странице (09.09.2026).

    До правки отказ сервера - счёт удалён, чужой заказчик, опечатка в адресе -
    показывал одну красную плашку посреди пустоты: ни заголовка, ни пути назад.
    Единственным выходом оставалась кнопка браузера. Шапка и ссылка «К реестру»
    остаются на месте всегда: это §4.5.6, третий вопрос - «что делать сейчас».
  */
  if (!card) {
    return (
      <>
        <header className="head">
          <div>
            <h1 className="head__title">Лицевой счёт</h1>
          </div>
          <Link to="/registry" className="button button--quiet">
            К реестру
          </Link>
        </header>
        {error ? <Notice error={error} wide /> : <Loading what="Собираем карточку" />}
      </>
    )
  }

  // Действия, а не просмотр: при «только чтение» кнопок нет, а причину объясняет плашка в каркасе.
  const canAct = can(profile, 'manage_stages')
  const canEdit = can(profile, 'build_documents')
  const assemble = assemblyOf(profile) // закрыта ярусом «Под ключ» - вместо кнопок пояснение
  const tabs = tabsFor(card, canAct)
  /*
    Открывается первая доступная вкладка: у того, кто ведёт дело, это «Суд», у
    оператора коммуникаций - «Должники» (вкладок процесса у него нет вовсе).
    Неизвестная вкладка в адресе - опечатка, старая ссылка, отнятое право - не
    ошибка: человек попадает туда же, а не на пустой экран.
  */
  const fallback = tabs[0].key
  const wanted = params.get('tab')
  const tab = tabs.some((item) => item.key === wanted) ? wanted : fallback

  /*
    Переход на другую вкладку гасит отметку об успехе: «Состав должников
    подтверждён» над формой заведения приказа человек читает как отчёт о том,
    что он делает сейчас (то же правило применено к вкладкам настроек
    09.09.2026).
  */
  const changeTab = (key) => {
    setParams(key === fallback ? {} : { tab: key }, { replace: true })
    setNotice(null)
  }
  const go = (key, stage = null) => {
    // С 20.10.2026 стадия меняется окном из шапки: вкладку переключать не надо.
    if (!stage) changeTab(key)
    setGoal({ stage, at: Date.now() })
  }

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Лицевой счёт № {card.number}</h1>
          <div className="head__note">{card.address}</div>
          {canAct ? (
            <AccountStage card={card} onDone={done} suggested={goal?.stage ? goal : null} />
          ) : (
            <div className="head__note">Стадия: {card.stage_title}</div>
          )}
        </div>
        {/* Под кнопками - «Почему система решила так» (21.10.2026, указание владельца:
            туда, где на шапке было пусто). */}
        <div className="head__side">
          <div className="row">
            <AccountContactActions card={card} profile={profile} />
            {canEdit ? <AccountEditButton card={card} onDone={done} /> : null}
            <Link to="/registry" className="button button--quiet">
              К реестру
            </Link>
            <AccountRemoveButton card={card} profile={profile} />
          </div>
          <AccountWhy triage={card.triage} />
        </div>
      </header>

      {/* Сбой перечитывания после действия: карточка остаётся, плашка над ней
          (аудит 26.10.2026, причина 1). */}
      {error ? <Notice error={error} wide /> : null}
      {notice ? <Notice kind={notice.kind}>{notice.text}</Notice> : null}

      {/* Находки реестра банкротств - в шапке, на любой вкладке (27.10.2026, вариант Б):
          они останавливают подачу по счёту, и узнавать об этом после отказа сервера поздно.
          Решает тот, кто подаёт заявления. */}
      {canEdit ? (
        <AccountRegistryFindings accountId={card.account_id} version={card} onDone={done} />
      ) : null}

      {/*
        Выше сумм и вне вкладок намеренно: вопрос «можно ли сейчас обращаться по
        этому счёту» человек задаёт раньше, чем «сколько взыскиваем», а запрет,
        спрятанный за вкладку, означал бы звонок тому, кто просил не звонить.
        Блока нет вовсе, пока должник ничего не заявлял.
      */}
      <ContactState state={card.contact_state} />

      {/* «Счёт ждёт человека» с кнопкой «Вернуть в автоматику» - над суммами,
          рядом с «придержан» (решение владельца 06.10.2026). */}
      <NeedsHumanNotice card={card} profile={profile} onDone={done} />

      {/* Плашки «придержан» и «примечание УК» - над суммами (вариант Б1,
          05.10.2026), таблица «Дела по счёту» - под ними (указание владельца
          06.10.2026: сначала «Долг к взысканию»). Суммы передаются внутрь,
          чтобы встать между ними. */}
      <CustomerWork
        accountId={card.account_id}
        canAct={canAct}
        readonly={profile.is_readonly}
        onChanged={reload}
        onGo={go}
        version={card}
      >
        <AccountSummary card={card} />
      </CustomerWork>

      <div id="account-tabs" className="account-tabs">
        <Tabs tabs={tabs} active={tab} onChange={changeTab} />
      </div>

      {canAct ? (
        <>
          <TabPanel tabKey="court" active={tab}>
            <AccountCourtTab
              card={card}
              sections={sections}
              sectionsError={sectionsError}
              canEdit={canEdit}
              assemble={assemble}
              onDone={done}
            />
          </TabPanel>

          <TabPanel tabKey="order" active={tab}>
            <AccountOrderTab
              card={card}
              sections={sections}
              sectionsError={sectionsError}
              assemble={assemble}
              onDone={done}
            />
          </TabPanel>
        </>
      ) : null}

      <TabPanel tabKey="people" active={tab}>
        <AccountPeopleTab card={card} canEdit={canEdit} onDone={done} />
      </TabPanel>

      <AccountEntryTabs accountId={card.account_id} active={tab} canEdit={canEdit} onDone={done} />

      <TabPanel tabKey="history" active={tab}>
        <AccountTimeline accountId={card.account_id} />
      </TabPanel>
    </>
  )
}
